import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, Trash2, Plus, Loader2, CheckCircle2, XCircle, Clock,
  Shield, History,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending_approval: { cls: 'bg-amber-100 text-amber-800', icon: Clock,    label: 'Pending Approval' },
  executing:        { cls: 'bg-blue-100 text-blue-800',   icon: Loader2,  label: 'Executing' },
  completed:        { cls: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2, label: 'Completed' },
  rejected:         { cls: 'bg-red-100 text-red-800',     icon: XCircle,  label: 'Rejected' },
  cancelled:        { cls: 'bg-slate-100 text-slate-700', icon: XCircle,  label: 'Cancelled' },
};

const fmt = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return s; } };

export default function DataReset() {
  const [me, setMe] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [decideRow, setDecideRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, s, r, a] = await Promise.all([
        axios.get(`${API_URL}/auth/me`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/data-reset/scopes`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/data-reset/requests`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/data-reset/audit`, { withCredentials: true }).catch(() => ({ data: { audit: [] } })),
      ]);
      setMe(m.data); setScopes(s.data.scopes || []);
      setRequests(r.data.requests || []); setAudit(a.data.audit || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="data-reset-page">
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="text-red-600" size={28} /> Bulk Data Reset
          </h1>
          <p className="text-gray-600 mt-1">
            Wipe employee/attendance/biometric/CTC data with <b>dual approval</b>.
            Other masters (designations · leave policy · holidays · payroll heads · banks) are NEVER touched.
          </p>
        </div>
        <button onClick={() => setShowNew(true)}
          data-testid="new-reset-btn"
          className="px-5 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium flex items-center gap-2 shadow hover:bg-red-700">
          <Plus size={16} /> New Reset Request
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-4">
        ⚠️ This is destructive. Two different admins must approve. Audit log preserves every reset for forever.
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="px-4 py-2 bg-slate-50 text-xs uppercase text-slate-600 font-medium flex items-center gap-2">
          <History size={14} /> Reset Requests
        </div>
        {loading ? <div className="p-6 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" size={14} />Loading…</div>
          : requests.length === 0 ? <div className="p-10 text-center text-slate-500">No reset requests yet.</div>
            : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Requested</th>
                    <th className="text-left px-3 py-2">Scopes</th>
                    <th className="text-left px-3 py-2">Reason</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Approver</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => {
                    const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending_approval;
                    const Icon = s.icon;
                    const isMine = r.requested_by_user_id === me?.id;
                    return (
                      <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`reset-row-${r.id}`}>
                        <td className="px-3 py-2">
                          <div className="text-xs">{fmt(r.requested_at)}</div>
                          <div className="text-xs text-slate-500">{r.requested_by_name}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(r.scopes || []).map(sc => (
                              <span key={sc} className="px-2 py-0.5 rounded bg-slate-100 text-xs">{sc.replace('_', ' ')}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 max-w-xs truncate" title={r.reason}>{r.reason}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium inline-flex items-center gap-1 ${s.cls}`}>
                            <Icon size={11} /> {s.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{r.approved_by_name || r.rejected_by_name || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          {r.status === 'pending_approval' && !isMine && (
                            <button onClick={() => setDecideRow(r)} data-testid={`decide-${r.id}`}
                              className="px-3 py-1 rounded bg-amber-500 text-white text-xs font-medium">
                              Approve / Reject
                            </button>
                          )}
                          {r.status === 'pending_approval' && isMine && (
                            <button onClick={() => cancel(r.id, load)}
                              className="px-3 py-1 rounded bg-slate-200 text-slate-700 text-xs">
                              Cancel
                            </button>
                          )}
                          {r.status === 'completed' && (
                            <button onClick={() => alert(JSON.stringify(r.execution_summary, null, 2))}
                              className="text-blue-600 text-xs hover:underline">View summary</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
      </div>

      {audit.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 text-xs uppercase text-slate-600 font-medium">Audit Trail</div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Executed By</th>
                <th className="text-left px-3 py-2">Scopes</th>
                <th className="text-right px-3 py-2">Total Deleted</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a, i) => {
                const total = Object.values(a.summary?.deleted_by_collection || {}).reduce((s, n) => s + n, 0);
                return (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-1.5">{fmt(a.executed_at)}</td>
                    <td className="px-3 py-1.5">{a.executed_by_name}</td>
                    <td className="px-3 py-1.5">{(a.scopes || []).join(', ')}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-rose-700">{total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewRequestModal scopes={scopes} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {decideRow && <DecideModal req={decideRow} onClose={() => setDecideRow(null)} onDone={() => { setDecideRow(null); load(); }} />}
    </div>
  );
}

async function cancel(id, reload) {
  if (!window.confirm('Cancel this request?')) return;
  try {
    await axios.post(`${API_URL}/admin/data-reset/requests/${id}/cancel`, {}, { withCredentials: true });
    toast.success('Cancelled'); reload();
  } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
}

function NewRequestModal({ scopes, onClose, onCreated }) {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [picked, setPicked] = useState([]);
  const [reason, setReason] = useState('');
  const [deleteUsers, setDeleteUsers] = useState(true);
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(Array.isArray(data) ? data : (data.companies || []));
      } catch { /* */ }
    })();
  }, []);

  const toggle = (k) => setPicked(p => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);

  const submit = async () => {
    if (picked.length === 0) { toast.error('Pick at least one scope'); return; }
    if (reason.trim().length < 10) { toast.error('Reason must be at least 10 characters'); return; }
    if (confirm !== 'RESET') { toast.error('Type RESET to confirm'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/admin/data-reset/requests`, {
        scopes: picked, company_id: companyId || null, reason,
        delete_employee_user_accounts: deleteUsers,
      }, { withCredentials: true });
      toast.success('Reset request created — pending approval by another admin');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="new-reset-modal">
        <div className="p-5 border-b border-red-200 bg-red-50">
          <h2 className="font-bold text-red-800 flex items-center gap-2">
            <AlertTriangle size={20} /> New Bulk Data Reset Request
          </h2>
          <p className="text-xs text-red-700 mt-1">This is destructive. Another admin must approve before execution.</p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-700">Company (optional — leave blank for tenant-wide)</label>
            <select value={companyId} onChange={e => setCompanyId(e.target.value)}
              data-testid="reset-company"
              className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm">
              <option value="">— All companies in this tenant —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Scopes to wipe *</label>
            <div className="mt-2 space-y-2">
              {scopes.map(s => (
                <label key={s.key} className="flex items-start gap-2 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={picked.includes(s.key)}
                    onChange={() => toggle(s.key)}
                    data-testid={`scope-${s.key}`}
                    className="mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{s.label}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Touches: {s.collections.join(', ')}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {picked.includes('employees') && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={deleteUsers} onChange={e => setDeleteUsers(e.target.checked)} />
              Also delete employee user accounts (admins are kept regardless)
            </label>
          )}

          <div>
            <label className="text-xs font-medium text-slate-700">Reason *</label>
            <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
              data-testid="reset-reason"
              className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
              placeholder="e.g. Test data uploaded for April — fresh start before live cutover" />
            <div className="text-xs text-slate-500 mt-1">{reason.length}/1000 (min 10)</div>
          </div>

          <div>
            <label className="text-xs font-medium text-red-700">Type RESET to confirm *</label>
            <input value={confirm} onChange={e => setConfirm(e.target.value)}
              data-testid="reset-confirm"
              className="w-full mt-1 border border-red-300 rounded-lg px-3 py-2 text-sm font-mono"
              placeholder="RESET" />
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm">Cancel</button>
          <button onClick={submit} disabled={submitting || confirm !== 'RESET' || picked.length === 0 || reason.trim().length < 10}
            data-testid="reset-submit"
            className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );
}

function DecideModal({ req, onClose, onDone }) {
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  const decide = async (decision) => {
    if (decision === 'reject' && remarks.trim().length < 3) {
      toast.error('Add a short rejection remark'); return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/data-reset/requests/${req.id}/decide`,
        { decision, remarks: remarks.trim() || undefined }, { withCredentials: true });
      if (decision === 'approve') {
        const total = Object.values(data.summary?.deleted_by_collection || {}).reduce((s, n) => s + n, 0);
        toast.success(`Executed — ${total} records deleted`);
      } else { toast.success('Rejected'); }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const counts = req.preview_counts || {};

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="decide-modal">
        <div className="p-5 border-b border-amber-200 bg-amber-50">
          <h2 className="font-bold text-amber-800 flex items-center gap-2">
            <AlertTriangle size={20} /> Approve / Reject Reset Request
          </h2>
          <div className="text-xs text-amber-700 mt-1">
            Requested by <b>{req.requested_by_name}</b> on {fmt(req.requested_at)}
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-xs uppercase text-slate-500 font-medium mb-1">Reason</div>
            <div className="text-sm bg-slate-50 rounded-lg p-3">{req.reason}</div>
          </div>

          <div>
            <div className="text-xs uppercase text-slate-500 font-medium mb-1">Scopes</div>
            <div className="flex flex-wrap gap-1">
              {(req.scopes || []).map(s => <span key={s} className="px-2 py-0.5 rounded bg-rose-100 text-rose-700 text-xs">{s}</span>)}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase text-slate-500 font-medium mb-2">Preview — what will be deleted</div>
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm font-mono">
              <div>Employees in scope: <b>{counts.employees_in_scope || 0}</b></div>
              {Object.entries(counts).filter(([k]) => k !== 'employees_in_scope').map(([k, v]) => (
                <div key={k} className="text-xs">{k}: <b className="text-rose-700">{v}</b></div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Approver Remarks (required for reject)</label>
            <textarea rows={2} value={remarks} onChange={e => setRemarks(e.target.value)}
              data-testid="decide-remarks"
              className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm">Close</button>
          <button onClick={() => decide('reject')} disabled={busy}
            data-testid="decide-reject"
            className="px-4 py-2 rounded-lg bg-red-100 text-red-700 text-sm font-medium">
            Reject
          </button>
          <button onClick={() => decide('approve')} disabled={busy}
            data-testid="decide-approve"
            className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Approve & Execute
          </button>
        </div>
      </div>
    </div>
  );
}
